// Libs
import React from 'react';
import { useI18next, useTranslation } from 'gatsby-plugin-react-i18next';
import { StaticImage } from 'gatsby-plugin-image';
import QueryStringNavigate from '../QueryStringNavigate';

import {
  linkedinPath,
  facebookPath,
  instagramFirstPartPath,
  instagramSecondPartPath,
  youtubePath,
} from '../../images/media/mediaPaths';

import BlockLinks from './BlockLinks';
import Offices from './Offices';
import LetsTalk from './LetsTalk';

// Styles
import * as S from './Footer.styled';

import { paths } from '../../helpers/WordPressPath';
import * as urlHelper from '../../helpers/url.helper';
import { useViewport } from '../../hooks/useViewport';

const Footer = () => {
  const { t } = useTranslation(['footer'], { useSuspense: false });
  const { isPhone, isPhoneLandscape, isTablet, isDesktop, isHighRes } =
    useViewport();
  const { language } = useI18next();
  const sunheroLogoWhite = '../../images/sunheroLogoTransparent.svg';
  const logoDelMinisterio = '../../images/Footer/REA_Badge_Outlined.jpg';
  const popularLinks = [
    {
      label: 'Solar panels calculator',
      href: paths.solarCalculator[language],
    },
    {
      label: 'Solar self-consumption',
      href: paths.solarSelfConsumption2[language],
    },
    {
      label: 'Subsidies and grants',
      href: paths.subsidies[language],
    },
  ];
  const aboutUsLinks = [
    {
      label: 'Blog',
      href: paths.blog[language],
    },
    {
      label: 'Newsroom',
      href: paths.newsroom[language],
    },
    {
      label: 'Work with us',
      href: paths.careers[language],
    },
    {
      label: 'Contact',
      href: urlHelper.getPathForOtherLanguage('/contacto/', language),
    },
  ];

  return (
    <S.FooterWrapper>
      <S.FooterContainer>
        <S.LogoBlock>
          <QueryStringNavigate
            path="/"
            aria-current="page"
            className="footer-logo w-inline-block w--current"
          >
            <StaticImage
              src={sunheroLogoWhite}
              loading="lazy"
              alt="Sunhero Logo"
            />
          </QueryStringNavigate>
          <S.LogoText>
            {t('Powering the planet.\n One roof at a time')}
          </S.LogoText>
        </S.LogoBlock>
        <S.FooterContent>
          {(isPhone || isPhoneLandscape) && (
            <>
              <BlockLinks linkList={popularLinks} label="Popular" />
              <BlockLinks linkList={aboutUsLinks} label="ABOUT US" />
              <Offices />
              <LetsTalk />
            </>
          )}
          {isTablet && (
            <>
              <S.Column gap="70px">
                <BlockLinks linkList={popularLinks} label="Popular" />
                <Offices />
              </S.Column>
              <S.Column>
                <BlockLinks linkList={aboutUsLinks} label="ABOUT US" />

                <LetsTalk />
              </S.Column>
            </>
          )}
          {(isDesktop || isHighRes) && (
            <>
              <S.Column>
                <BlockLinks linkList={popularLinks} label="Popular" />
                <BlockLinks linkList={aboutUsLinks} label="ABOUT US" />
              </S.Column>
              <S.Column>
                <Offices />
              </S.Column>
              <S.Column>
                <LetsTalk />
              </S.Column>
            </>
          )}
        </S.FooterContent>
        <S.LicenseBlock>
          <div>
            <StaticImage
              src={logoDelMinisterio}
              loading="lazy"
              alt="logoDelMinisterio"
            />
          </div>
          <S.MediaBlock>
            <a
              data-cy="instagram"
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.instagram.com/sunherosolar/"
            >
              <S.MediaItem>
                <S.StyledSVG
                  xmlns="http://www.w3.org/2000/svg"
                  width="29"
                  height="29"
                  viewBox="0 0 29 29"
                  alt="Instagram"
                >
                  <path d={instagramFirstPartPath} />
                  <path d={instagramSecondPartPath} />
                </S.StyledSVG>
                <S.BackgroundCircle />
              </S.MediaItem>
            </a>
            <a
              data-cy="youtube"
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.youtube.com/@sunherosolar"
            >
              <S.MediaItem>
                <S.StyledSVG
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="29"
                  viewBox="0 0 28 29"
                  alt="Youtube"
                >
                  <path fillRule="evenodd" clipRule="evenodd" d={youtubePath} />
                </S.StyledSVG>
                <S.BackgroundCircle />
              </S.MediaItem>
            </a>
            <a
              data-cy="linkedin"
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.linkedin.com/company/sunhero"
            >
              <S.MediaItem>
                <S.StyledSVG
                  xmlns="http://www.w3.org/2000/svg"
                  width="29"
                  height="29"
                  viewBox="0 0 29 29"
                  alt="LinkedIn"
                >
                  <path d={linkedinPath} />
                </S.StyledSVG>
                <S.BackgroundCircle />
              </S.MediaItem>
            </a>
            <a
              data-cy="facebook"
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.facebook.com/sunherosolar"
            >
              <S.MediaItem>
                <S.StyledSVG
                  xmlns="http://www.w3.org/2000/svg"
                  width="29"
                  height="29"
                  viewBox="0 0 29 29"
                  alt="Facebook"
                >
                  <path d={facebookPath} />
                </S.StyledSVG>
                <S.BackgroundCircle />
              </S.MediaItem>
            </a>
          </S.MediaBlock>
        </S.LicenseBlock>
        <S.FooterLinkBlock>
          <S.FooterLink>
            © {new Date().getFullYear()}{' '}
            {t('Sunhero Spain, S.L.U. All rights reserved')}
          </S.FooterLink>
          <S.PolicyLinks>
            <QueryStringNavigate
              className="footerLink"
              path="/terms-and-conditions/legal-notice"
            >
              {t('Legal notice')}
            </QueryStringNavigate>
            <QueryStringNavigate
              className="footerLink"
              path="/terms-and-conditions/privacy-policy"
            >
              {t('Privacy policy')}
            </QueryStringNavigate>
            <QueryStringNavigate
              className="footerLink"
              path="/terms-and-conditions/cookie-policy"
            >
              {t('Cookies policy')}
            </QueryStringNavigate>
            <QueryStringNavigate
              className="footerLink"
              path="https://sunhero.trusty.report/"
              id="whistleblowing"
              target="blank"
            >
              {t('whistleblowing')}
            </QueryStringNavigate>
          </S.PolicyLinks>
        </S.FooterLinkBlock>
      </S.FooterContainer>
    </S.FooterWrapper>
  );
};

export default Footer;
